import React, {
  RefObject,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Dimensions, Platform, StyleSheet, View } from 'react-native';

import {
  GetSizeClassV2,
  SizeClassV2,
} from '../../../../contexts/SizeClassContext';
import { PlayerControlsRef } from '../Controls';
import { PlayerVideoRef } from '../PlayerVideo/PlayerVideo.types';
import {
  toggleFullscreenUtility,
  useFullscreenNativeChangeListener,
  useFullscreenOrientationListener,
} from './fullscreenUtils';

export interface FullscreenWrapperRef {
  toggleFullscreen(): void;
  enterFullscreen(): void;
  exitFullscreen(): void;
  getIsFullscreen(): boolean;
}

interface FullscreenWrapperProps {
  children:
    | React.ReactNode
    | ((
        childFuncProps: Omit<FullscreenWrapperRef, 'getIsFullscreen'> & {
          isFullscreen: boolean;
        }
      ) => React.ReactNode);
  shouldStretchFullscreen?: boolean;
  playerVideoRef: RefObject<PlayerVideoRef | null>;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  playerControlsRef?: RefObject<PlayerControlsRef | null>;
}

const VIDEO_RATIO = 16 / 9;

const FullscreenWrapper = React.forwardRef<
  FullscreenWrapperRef,
  FullscreenWrapperProps
>(
  (
    {
      children,
      shouldStretchFullscreen,
      playerVideoRef,
      onFullscreenChange,
      playerControlsRef,
    },
    fullscreenWrapperRef
  ) => {
    const outerViewRef = useRef<View>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = useCallback(
      ({ forceTo }: { forceTo?: boolean } = {}) => {
        const willFullscreen = forceTo !== undefined ? forceTo : !isFullscreen;
        toggleFullscreenUtility({
          playerVideoRef,
          willFullscreen,
          callback: () => {
            setIsFullscreen(willFullscreen);

            /** This is not needed for the web because its already handled in useFullscreenNativeChangeListener */
            if (Platform.OS !== 'web') {
              onFullscreenChange?.(willFullscreen);
            }
          },
        });
      },
      [isFullscreen, onFullscreenChange, playerVideoRef]
    );

    useFullscreenOrientationListener((shouldFullscreen) => {
      const { height } = Dimensions.get('window');
      const rotatedSizeClassV2 = GetSizeClassV2(height);

      const isSmall = rotatedSizeClassV2 < SizeClassV2.L;

      // /** Do not auto fullscreen if the width after rotation is equal or greater than large breakpoint */
      if (!isSmall) return;

      if (!isFullscreen && shouldFullscreen) {
        toggleFullscreen({ forceTo: true });
      } else if (isFullscreen && !shouldFullscreen) {
        toggleFullscreen({ forceTo: false });
      }
    });

    useFullscreenNativeChangeListener((isItFullscreen) => {
      /** Update our tracked property if the native value changes. */
      setIsFullscreen(isItFullscreen);
      onFullscreenChange?.(isItFullscreen);
    }, playerControlsRef);

    useImperativeHandle(
      fullscreenWrapperRef,
      () => ({
        toggleFullscreen,
        enterFullscreen: () => toggleFullscreen({ forceTo: true }),
        exitFullscreen: () => toggleFullscreen({ forceTo: false }),
        getIsFullscreen: () => isFullscreen,
      }),
      [toggleFullscreen, isFullscreen]
    );

    return (
      <View
        ref={outerViewRef}
        style={[
          styles.wrapper,
          isFullscreen || shouldStretchFullscreen
            ? styles.wrapperWithFullscreen
            : styles.wrapperWithoutFullscreen,
        ]}
      >
        {typeof children === 'function'
          ? children({
              isFullscreen,
              toggleFullscreen,
              enterFullscreen: () => toggleFullscreen({ forceTo: true }),
              exitFullscreen: () => toggleFullscreen({ forceTo: false }),
            })
          : children}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    maxHeight: '100%',
    maxWidth: '100%',
    position: 'relative',
  },
  wrapperWithFullscreen: {
    height: '100%',
    width: '100%',
  },
  wrapperWithoutFullscreen: {
    aspectRatio: VIDEO_RATIO,
  },
});

export default FullscreenWrapper;
